import clsx from "clsx";

type PillSize = "sm" | "md" | "lg";

const sizeClassMapping: Record<
  PillSize,
  { pill: string; gap: string; text: string }
> = {
  sm: { pill: "px-2 py-0.5", gap: "gap-1", text: "text-xs" },
  md: { pill: "px-4 py-1", gap: "gap-2", text: "text-sm" },
  // The design system's dire-chip: as tall as a button, semibold label.
  lg: {
    pill: "h-10 px-3 py-2",
    gap: "gap-1",
    text: "text-body-lg font-semibold leading-none",
  },
};

interface PillProps {
  text: string | number;
  icon?: React.ReactNode;
  /** Which side of the text the icon sits on. Defaults to "leading". */
  iconPosition?: "leading" | "trailing";
  size?: PillSize;
  /**
   * Accessible name for pills whose visible text does not carry the meaning on
   * its own, e.g. a "+3" overflow chip.
   */
  ariaLabel?: string;
  className?: string;
}

export const Pill: React.FC<PillProps> = ({
  text,
  icon,
  iconPosition = "leading",
  size = "md",
  ariaLabel,
  className,
}) => {
  const sizeClasses = sizeClassMapping[size];

  return (
    <div
      // A plain `div` is not a naming-capable element, so `aria-label` alone
      // would be ignored — `role="img"` makes the chip announce as its label
      // instead of as the bare glyph.
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
      className={clsx(
        "inline-flex shrink-0 items-center whitespace-nowrap rounded-full",
        sizeClasses.pill,
        sizeClasses.gap,
        className,
      )}
    >
      <div className={clsx("flex items-center", sizeClasses.gap)}>
        {iconPosition === "leading" && icon}
        <span className={sizeClasses.text}>{text}</span>
        {iconPosition === "trailing" && icon}
      </div>
    </div>
  );
};
